import React, { useEffect, createContext, useContext, useCallback, useMemo, useState } from "react";
import {
	onAuthStateChanged,
	signInWithEmailAndPassword,
	signOut,
	sendPasswordResetEmail,
	confirmPasswordReset,
	sendSignInLinkToEmail,
	isSignInWithEmailLink,
	signInWithEmailLink
} from "firebase/auth";

import { auth } from "../config/firebase/firebase";
import { POST_LOGIN_PARAM } from "utils/auth-redirect";
import { useLocalStorage } from "../hooks/useLocalStorage";
import routeNames from "../constants/route-names";
import { LoginStatus } from "../constants/login-status";
import { getApolloClient } from "../config/apollo/apollo";
import { clearLoggedUser } from "state/reactive-vars/logged-user-var";
import { clearAllAiChatStatus } from "state/reactive-vars/ai-chat-status-var";
import { clearAllUploadingFiles } from "state/reactive-vars/uploading-files-var";
import { clearSetupMeetingBannerDismissed } from "state/reactive-vars/setup-meeting-banner-var";
import { useChatWindowFiltersStore } from "state/zustand-stores/chat-window-filters-store";
import {
	useAdminOrganizationsFiltersStore,
	useAdminProductsFiltersStore,
	useAdminPropertiesFiltersStore,
	useAdminUsersFiltersStore,
	useInstalledProductsFiltersStore,
	useOrderFiltersStore
} from "state/zustand-stores/index";
import { usePostHog } from "posthog-js/react";

// Token refresh interval (50 minutes) - Firebase token expiration = 60 minutes
const TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000;

export type AuthContextProps = {
	firebaseAuth: any;
	loggedUser: any;
	token: string;
	loginStatus: string;
	login?: (data: { email: string; password: string }, redirectTo?: string) => Promise<boolean>;
	logout?: (withNavigation: boolean) => void;
	setLoggedUser?: (user: any) => void;
	startPasswordReset?: (email: string) => Promise<boolean>;
	verifyPasswordResetCode?: (code: string, newPassword: string) => Promise<boolean>;
	sendMagicLink?: (email: string, redirectTo?: string) => Promise<boolean>;
	verifyMagicLink?: (emailLink: string, email: string) => Promise<boolean>;
	updateToken?: (user: any) => Promise<void>;
	refreshToken?: () => Promise<void>;
};

/*
We use ContextAPI to obtain the logged-in user, if any,
and pass it to the child components of our application.
*/
export const AuthContext = createContext<AuthContextProps>({
	firebaseAuth: auth,
	loggedUser: null,
	token: "",
	loginStatus: LoginStatus.LOADING
});

// This useAuth Hook exposes the user's state and methods for user login and logout.
export const useAuth = () => {
	return useContext(AuthContext);
};

const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [loggedUser, setLoggedUser] = useLocalStorage("firebaseUser", null);
	const [loginStatus, setLoginStatus] = useState(LoginStatus.LOADING);
	const [token, setToken] = useState("");
	const posthog = usePostHog();

	const clearAdminOrganizationsFilters = useAdminOrganizationsFiltersStore(state => state.clearFilters);
	const clearAdminProductsFilters = useAdminProductsFiltersStore(state => state.clearFilters);
	const clearAdminPropertiesFilters = useAdminPropertiesFiltersStore(state => state.clearFilters);
	const clearAdminUsersFilters = useAdminUsersFiltersStore(state => state.clearFilters);
	const clearInstalledProductsFilters = useInstalledProductsFiltersStore(state => state.clearFilters);
	const clearOrderFilters = useOrderFiltersStore(state => state.clearFilters);
	const clearChatWindowFilters = useChatWindowFiltersStore(state => state.clearFilters);

	const resetGlobalState = useCallback(() => {
		clearAdminOrganizationsFilters();
		clearAdminProductsFilters();
		clearAdminPropertiesFilters();
		clearAdminUsersFilters();
		clearInstalledProductsFilters();
		clearOrderFilters();
		clearChatWindowFilters();

		clearLoggedUser(); // Clear the logged user data from reactive var

		clearAllAiChatStatus();
		clearAllUploadingFiles();
		clearSetupMeetingBannerDismissed();

		// Remove both storages to ensure clean logout
		localStorage.removeItem("firebaseUser");
		localStorage.removeItem("loggedUser");
	}, [
		clearAdminOrganizationsFilters,
		clearAdminProductsFilters,
		clearAdminPropertiesFilters,
		clearAdminUsersFilters,
		clearInstalledProductsFilters,
		clearOrderFilters,
		clearChatWindowFilters
	]);

	// Function to get and update token
	const updateToken = useCallback(async (user: any) => {
		if (!user) return;
		try {
			const newToken = await user.getIdToken(true);
			setToken(newToken);
			const apolloClient = getApolloClient(true, newToken);
			// Reset Apollo Client's store with new token
			await apolloClient.resetStore();
		} catch (error) {
			console.error("Error updating token:", error);
		}
	}, []);

	// Function to get and update token
	const refreshToken = useCallback(async () => {
		if (!loggedUser) return;
		try {
			const newToken = await loggedUser.getIdToken(true);
			setToken(newToken);
			const apolloClient = getApolloClient(true, newToken);
			// Reset Apollo Client's store with new token
			await apolloClient.resetStore();
		} catch (error) {
			console.error("Error updating token:", error);
		}
	}, [loggedUser]);

	// call this function to sign out logged in user
	const logout = useCallback(
		async (withNavigation: boolean = true, route: string = routeNames.LOGIN) => {
			try {
				await signOut(auth);
				setLoggedUser(null);
				setToken("");
				setLoginStatus(LoginStatus.OUT);

				resetGlobalState();

				if (withNavigation) {
					// custom event to do something like "navigate(routeNames.LOGIN, { replace: true });" from outside of Router.
					window.dispatchEvent(
						new CustomEvent("authNavigation", {
							detail: { route }
						})
					);
				}
			} catch (error) {
				console.log("Firebase logout error: ", error);
			}
		},
		[auth, setLoggedUser, resetGlobalState]
	);

	// Set up periodic token refresh
	useEffect(() => {
		if (!loggedUser) return;

		const refreshTokenInterval = setInterval(() => {
			updateToken(loggedUser);
		}, TOKEN_REFRESH_INTERVAL);

		return () => clearInterval(refreshTokenInterval);
	}, [loggedUser, updateToken]);

	useEffect(() => {
		/*
    Firebase provides the onAuthStateChanged method, which is an observer of the global authentication object.
    This observer is called every time the user's login state changes.
    If the user is logged in, the callback will receive the user's properties as a parameter; otherwise it will receive null.
    */
		const unsubscribe = onAuthStateChanged(auth, async user => {
			if (user) {
				// User is signed in, see docs for a list of available properties
				// https://firebase.google.com/docs/reference/js/auth.user
				const { uid, metadata: firebaseUserMetadata, emailVerified } = user || {};
				const { creationTime, lastSignInTime } = firebaseUserMetadata || {};

				// console.log("onAuthStateChanged user: ", user);

				const userIsJustCreated = creationTime && lastSignInTime && creationTime === lastSignInTime;

				await updateToken(user);

				// Store Firebase user in localStorage
				setLoggedUser(user);

				// Set login status based on email verification
				if (!emailVerified) {
					setLoginStatus(LoginStatus.EMAIL_NOT_VERIFIED); // Add this new status
				} else if (userIsJustCreated) {
					setLoginStatus(LoginStatus.IN);
				} else {
					setLoginStatus(LoginStatus.IN);
				}
			} else {
				// User is signed out. user === null when user is not logged
				setLoggedUser(null);
				setToken("");
				setLoginStatus(LoginStatus.OUT);

				// Clear logged user data when Firebase user is null
				clearLoggedUser();

				clearAllAiChatStatus();
				clearAllUploadingFiles();
			}
		});

		return () => unsubscribe();
	}, [setLoggedUser]);

	useEffect(() => {
		if (posthog) {
			if (loggedUser) {
				posthog.identify(loggedUser.uid);
			} else {
				posthog.reset();
			}
		}
	}, [posthog, loggedUser]);

	// call this function when you want to authenticate the user.
	// `redirectTo` is the page the user was trying to open before the login challenge; without one
	// they land on the dashboard.
	const login = useCallback(
		async (data: { email: string; password: string }, redirectTo: string = routeNames.DASHBOARD) => {
			const { email, password } = data;

			try {
				const userCredential = await signInWithEmailAndPassword(auth, email, password);

				if (userCredential) {
					// Signed in
					const user = userCredential.user;
					setLoggedUser(user);
					const idToken = await user.getIdToken();
					setToken(idToken);

					const { emailVerified } = user || {};

					window.dispatchEvent(
						new CustomEvent("authNavigation", {
							detail: {
								route: emailVerified ? redirectTo : routeNames.EMAIL_VERIFICATION_PENDING
							}
						})
					);
					return true;
				}
				return false;
			} catch (error: any) {
				console.log("Firebase login error: ", error);
				return false;
			}
		},
		[auth, setLoggedUser]
	);

	const startPasswordReset = useCallback(
		async (email: string) => {
			// TODO use our custom SMTP service to send the email: https://firebase.google.com/docs/auth/admin/email-action-links?hl=es

			if (auth && email) {
				const appUrl = location.origin;
				const continueUrl = `${appUrl}${routeNames.LOGIN}`;

				const settings = {
					url: continueUrl,
					handleCodeInApp: true
				};

				try {
					// For security this doesn't fail if the email doesn't exist.
					// To change it: https://cloud.google.com/identity-platform/docs/admin/email-enumeration-protection?hl=es-419#disable
					await sendPasswordResetEmail(auth, email, settings);
					return true;
				} catch (error) {
					console.log("Firebase sendPasswordResetEmail error: ", error);
					return false;
				}
			} else {
				return false;
			}
		},
		[auth]
	);

	const verifyPasswordResetCode = useCallback(
		async (oobCode: string, newPassword: string) => {
			if (auth && oobCode && newPassword) {
				try {
					await confirmPasswordReset(auth, oobCode, newPassword);
					return true;
				} catch (error) {
					console.log("Firebase confirmPasswordReset error: ", error);
					return false;
				}
			} else {
				return false;
			}
		},
		[auth]
	);

	const sendMagicLink = useCallback(
		async (email: string, redirectTo?: string) => {
			if (auth && email) {
				try {
					const appUrl = location.origin;
					// The link opens the login screen in a fresh tab, so the page the user was trying to
					// reach has to travel inside it to survive.
					const continueParams = new URLSearchParams({ emailparam: email });
					if (redirectTo) continueParams.set(POST_LOGIN_PARAM, redirectTo);
					const continueUrl = `${appUrl}${routeNames.LOGIN}?${continueParams.toString()}`;

					const actionCodeSettings = {
						url: continueUrl,
						handleCodeInApp: true
					};

					await sendSignInLinkToEmail(auth, email, actionCodeSettings);
					return true;
				} catch (error) {
					console.log("Firebase sendMagicLink error: ", error);
					return false;
				}
			} else {
				return false;
			}
		},
		[auth]
	);

	const verifyMagicLink = useCallback(
		async (emailLink: string, email: string) => {
			if (auth && emailLink && email) {
				try {
					const isSignIn = isSignInWithEmailLink(auth, emailLink);

					if (isSignIn) {
						await signInWithEmailLink(auth, email, emailLink);
					}
					return true;
				} catch (error) {
					console.log("Firebase verifyMagicLink error: ", error);
					return false;
				}
			} else {
				return false;
			}
		},
		[auth]
	);

	const value = useMemo(() => {
		return {
			firebaseAuth: auth,
			loggedUser,
			setLoggedUser,
			token,
			loginStatus,
			login,
			logout,
			startPasswordReset,
			verifyPasswordResetCode,
			sendMagicLink,
			verifyMagicLink,
			updateToken,
			refreshToken
		};
	}, [loggedUser, token, loginStatus]);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContextProvider;
